/* eslint-disable max-lines */

import { WebVitalsMetric } from '../performance/types';

export interface TimeTrackingInfo {
  modifierSelected: 'AM' | 'PM' | 'AH';
  /** The start-time of the chosen time period as a 2-digit string in 24hr format */
  startTime: string;
  /** The end-time of the chosen time period as a 2-digit string in 24hr format */
  endTime: string;
}

/** Tracking data consumed by adobe launch */
export type TrackingData = {
  eventData?: {
    webVitals?: WebVitalsMetric[];
  };
  errorData?: {
    errorID?: string;
    errorMessage?: string;
    errorReason?: string;
  };
  flagr?: Record<string, string | undefined>;
  pageData?: {
    isTelehealthPage?: boolean;
    pagePath?: string;
    pageType?: PageType;
    section?: PageSection;
    subsection?: SubSection;
    subsubsection?: SubSubSection;
    error?: {
      type: string;
      message?: string;
    };
    /** Identifier if a react page. This is required as the analytics rule in Adobe Launch will need to know if the old or new text search rule should be run as these will be configured slightly differently due to the pages being a single page app (SPA). */
    isSPA?: true;
    language?: string;
  };
  partnerData?: {
    consumerOfferPartner?: string;
    consumerOfferTitle?: string;
    opi?: {
      slideIn?: {
        displayed?: boolean;
        question?: string;
        partner?: string;
      };
    };
  };
  resultsData?: {
    /** Data about profile appearances for practice-admin to consume.
     * Each key takes the form `${id}:${appointmentCount},${id}:${appointmentCount}` ...etc
     */
    appearanceTracking?: {
      practices?: string;
      practitioners?: string;
      practiceNearbyAppointments?: string;
      practiceStillLooking?: string;
    };
    /** The total number of appointments returned for the given search query across all pagination levels */
    appointmentsReturned?: number;
    /** The number of extended search results displayed on page. */
    extendedDisplayed?: number;
    /** radius - same date The extended search logic used to return the extended search results. Can either be one of the following - 'same criteria - no date', 'same criteria and date - increased radius' or 'increased radius - no date'. More detail can be found in JIRA ticket GROW-471 */
    extendedLogic?: ExtendedLogic;
    /** The total number of returned results for extended search that matched the logic conditions. Can probably remove as not relevant. */
    extendedReturned?: number;
    /*
     * Array of all the Practice IDs that have featured listings displayed on the viewed results page. */
    featuredPracticeList?: number[];
    /** The number of featured listings displayed on the search result page. */
    featuredResultsDisplayed?: number;
    /** Do any of the practices displayed in the results offer HICAPS or not? */
    hasHicaps?: boolean;
    /** Do any of the practices displayed in the results have interest data against them? */
    hasInterests?: boolean;
    /** Do any of the practices displayed in the results have preferred Health Insurance Provider data ? */
    hasPreferredProvider?: boolean;
    /** Do any of the practices displayed in the results have pricing data? */
    hasPricing?: boolean;
    /** Do any of the practices displayed in the results show the limited appointments message (i.e. "Only x left")? */
    limitedAppointmentsMsgShown?: boolean;
    /** The number of search result pages available for the search query. (i.e. if 3 pages of results value would be 3) */
    paginationAvailable?: number;
    /** Identifies the current search result page pagination level the user is in. */
    paginationLevel?: number;
    /** Array of all the Practice IDs for Practice search result cards that displayed on the viewed results page. */
    practiceList?: string[];
    /** The number of practices result cards displayed on the page within the regular results (i.e. excludes the extended search results) */
    practicesDisplayed?: number;
    /** The number of practices result cards returned in the query in total (across all pagination levels) for the regular results. */
    practicesReturned?: number;
    /** Array of all the Practitioner IDs for Practitioner search result cards that displayed on the viewed results page. Currently Practitioner cards don't exist on appointment search so this would be empty. */
    practitionerList?: string[];
    /** The number of practitioner result cards displayed on the page within the regular results (i.e. excludes the extended search results). Currently Practitioner cards don't exist on appointment search so this would be empty. */
    practitionersDisplayed?: number;
    /** The number of practitioner result cards returned in the query in total (across all pagination levels) for the regular results. Currently Practitioner cards don't exist on appointment search so this would be empty. */
    practitionersReturned?: number;
    /** The number of regular search results displayed on page. (i.e. excludes extended search) */
    resultsDisplayed?: number;
    /** The number of regular search results displayed on page. (i.e. excludes extended search) */
    resultsReturned?: number;
    /** The total number of result cards displayed on the page (includes both regular and extended results) Should change to only show total results that users can see (i.e. Exclude the extended search count of those returned but not displayed ever to the end user.). */
    totalDisplayed?: number;
    /** The total number of result cards returned for the search query across all pagination levels (includes both regular and extended results). */
    totalReturned?: number;
    /** All the specialties represented in the results */
    specialty?: string[];
    /** The total number of result cards displayed for search query on page when location filter selected */
    resultsInSuburbDisplayed?: number;
  };
  searchData?: {
    /** Has the after hours filter has been applied? */
    AHMod?: boolean;
    /** Has the bulk billing filter has been applied? */
    BBMod?: boolean;
    /** Identifies the date parameter for appointment search. (Note: Date format only exists for specific date searches outside of Today or Tomorrow) */
    date?: 'Today' | 'Next7Days' | 'Tomorrow' | 'NextAvailable' | SearchDateString;
    /** Identifies the postcode for the suburb the search query relates to. */
    postcode?: string;
    /** Practice Identifies the specialty the search query relates to. */
    specialty?: string;
    /** Identifies the state the search query relates to. */
    state?: string;
    /** Perth Identifies the suburb the search query relates to. */
    suburb?: string;
    /** Practice String containing the search term used or a generated search term. For Appointment Search, this just contains the specialty. */
    term?: string;
    /** Information about the filters that have been applied */
    filtersApplied?: {
      /** The number of filters that the user has applied (i.e. not in default state) for the returned results. Should be 0 when no filters have been applied. */
      count?: number;
      /** "Today", "Next7Days", "Tomorrow", "NextAvailable", "2019-03-13", or "2019-03-13 to 2019-03-15"  */
      date?: string;
      /** String containing either "M" for Male, "F" for Female, or "All" depending on which filter option has been applied. */
      gender?: 'M' | 'F' | 'All';
      /** Identifies the consultation type filter applied. Can either be "all", "telehealth", "athome" */
      consultationType?: ConsultationType;
      /** Identifier if the Online Appointments filter is active or not. true if active. false if not active. */
      onlineAppts?: boolean;
      /** Identifier if the specialty filter is active, the name of the specialty means its active */
      specialty?: string;
      /** The value of the time filter */
      time?: TimeTrackingInfo;
      /** Identifies if the bulkBill filter is active. True for results that have bulk billing */
      bulkBill?: boolean;
      /** Identifier for if the language filter is applied. A string of language means its active */
      practitionerLanguage?: string;
      /** Identifier if the Pricing Available filter is active or not. true if active. false if not active. */
      pricingAvailable?: boolean;
      /** Identifier if the Special Offer filter is active or not. true if active. false if not active. */
      specialOffers?: boolean;
      /** If a payment method filter is applied. Active if a string is returned */
      paymentMethod?: string;
      /** If a government scheme filter is applied. Active if a string is returned */
      govScheme?: string;
      /** Identifier if the Accepts For filter is active or not. true if active. false if not active. */
      acceptsNewPatients?: boolean;
      /** Identifier if the Hicaps filter is active or not. true if active. false if not active. */
      hicaps?: boolean;
      /** Identifier if the insurance filter is active, the name of the insurance means its active */
      preferredInsProvider?: string;
      /** Information about location filters */
      location?: {
        /** String to identify the type of location filter applied. If a state location filter was applied value would be "state", if a suburb then "suburb" and if a region then "region. */
        type?: 'state' | 'suburb' | 'region';
        /** String to identify the suburb of the location filter applied. If filtered by a state or region, this should be undefined. */
        suburb?: string;
        /** String to capture the postcode when filtered by a suburb. If filtered by a state or region, this should be undefined. */
        postcode?: string;
        /** String to capture the filtered state. */
        state?: string;
        /** String to capture the filtered region. If filtered by state, this should be undefined. */
        region?: string;
      };
      vaccineType?: string;
    };
  };
  userData?: {
    loggedIn?: LoggedInState;
    /** A hashed version of the user's ID */
    userId?: string;
    loading?: boolean;
    prefersColorScheme?: 'light' | 'dark' | 'no-preference' | 'unknown';
  };

  practiceData?: TrackingPracticeData;
  bookingData?: {
    allowsSymptomatic?: string;
    bookingID?: string;
    appointmentType?: string;
    newPatient?: boolean;
    stepName?: string;
    prevStepName?: string;
    practiceID?: string;
    practitionerID?: string;
    bookingSpecialty?: string;
    bookingForm?: BookingForm;
    process?: string;
    isReferralMandatory?: boolean;
    isTelehealth?: boolean;
    captchaShown?: boolean;
    appointmentId?: string;
    daysBetweenApptAndBooking?: DaysBetweenApptAndBooking | number;
    networkReferral?: string;
    roadblock?: {
      id?: string;
    };
    OptInMedDelivery?: {
      shown?: boolean;
      input?: string;
    };
    healthInterests?: {
      shown?: boolean;
      selectedCount?: string;
    };
    privacyPreference?: {
      shown?: boolean;
      input?: string;
    };
  };
  form?: {
    name?: string;
    stepName?: string;
    stepNo?: number;
    isStart?: boolean;
    isCompleted?: boolean;
    formFieldError?: {
      errorMessage?: string;
      errorFormField?: string;
    };
    interaction?: {
      [key: string]: unknown;
    };
  };
  semData?: {
    businessId?: string;
  };
  socialData?: {
    socialName?: string;
  };
};

export interface TrackingPracticeData {
  grpLocId?: string;
  practiceName?: string;
  state?: string;
  postcode?: string;
  suburb?: string;
  primarySpecialty?: string;
  specialties?: string[];
  bulkBilled?: boolean;
  afterHours?: boolean;
  paidProfile?: boolean;
  apptCount?: number;
  corporateProfile?: boolean;
  useBookNowButton?: boolean;
  rescripts?: boolean;
  ratingsData?: {
    // eslint-disable-next-line camelcase
    disable_ratings?: boolean;
    // eslint-disable-next-line camelcase
    num_responses?: number;
  };
  hasPM?: boolean;
  hasClaimed?: boolean;
  groupEstablishmentType?: string;
  isPatientEnquiryFormEnabled?: boolean;
  hasViewApptsInAppCta?: boolean;
  pricingGuideShown?: boolean;
  todayApptCount?: number;
  tomorrowApptCount?: number;
  apptCountNext30?: number;
  practitionerCount?: number;
  availablePractitionerCount?: number;
  isTelehealth?: boolean;
  isApptRanges?: boolean;
  isCbp?: string;
  isTestPractice?: string;
  preferredProviders?: {
    count: number;
    names: string[];
  };
  pricingVerified?: string;
  telehealth?: {
    enabled?: string;
    dedicated?: string;
  };
  isVerified?: boolean;
}

export enum LoggedInState {
  /** the user isn't logged in */
  ANONYMOUS = 'Anonymous',
  /** the user is logged in. */
  LOGGED_IN = 'Logged In',
}

type SearchDateString = string; // of the form 2019-08-07

enum ExtendedLogic {
  SAME_CRITERIA_NO_DATE = 'same criteria - no date',
  SAME_CRITERIA_INCREASED_RADIUS = 'same criteria and date - increased radius',
  INCREASED_RADIUS_NO_DATE = 'increased radius - no date',
}

export enum PageType {
  ERROR = 'Error',
  LANDING = 'Landing',
  HOMEPAGE = 'Homepage',
  LP = 'LP',
  OFFER_DETAIL = 'Offer Detail',
  PRACTICE = 'Practice',
  PRACTITIONER = 'Practitioner',
  /** Constant across all SERP pages as "Res" for Results. */
  SEARCH_RESULTS = 'Res',
  MEMBERS = 'Members',
}

export enum PageSection {
  /** Identifier for Consumer site. Constant across the marketplace website. */
  CONSUMER = 'Con',
  MEMBERS = 'Members',
  BOOKING = 'Booking',
}

export enum SubSection {
  APPOINTMENT_SEARCH = 'Appt',
  DIRECTORY_SEARCH = 'Dir',
  LANDING = 'Landing',
  OFFERS = 'Offers',
  PROFILE = 'Profile',
  FORM = 'Form',
  MEMBERS = 'Members',
  HOME = 'Home',
}

export enum SubSubSection {
  TELEHEALTH = 'Telehealth',
  BOOKINGS = 'Bookings',
  FAVOURITES = 'Favourites',
  INTERESTS = 'Interests',
}

export enum ConsultationType {
  ALL = 'all',
  TELEHEALTH = 'telehealth',
  AT_HOME = 'athome',
}

export enum DaysBetweenApptAndBooking {
  SAME_DAY = 'same day',
}

export enum BookingForm {
  ANDROID = 'android',
  IPHONE = 'iphone',
  PRACTICE = 'practice',
  WEBSITE = 'website',
  WIDGET = 'widget',
}

export enum TrackingRegion {
  COVID19_INFORMATION = 'COVID-19 Information',
  HEALTHCARE_PROVIDER = 'Healthcare provider',
  PRESCRIPTION_DELIVERY = 'Prescription delivery',
  ABOUT_HEALTHENGINE = 'About healthengine',
  DOWNLOAD_APP = 'Download app',
  POPULAR_SEARCHES = 'Popular searches',
  MY_CARE_TEAM = 'My Care Team',
  PHI = 'Private Health Insurance',
}

export enum ProcessId {
  ABOUT_PRACTICE = 'A',
  APPOINTMENT_OVERVIEW = 'O',
  APPOINTMENT_SELECTION = 'C',
  APPOINTMENT_TYPE = 'T',
  POLICY = 'P',
  PERSONAL_DETAILS = 'D',
  ADDITIONAL_PERSONAL_DETAILS = 'M',
  MEDICAL_HISTORY = 'H',
  PHARMACY = 'G',
  PAYMENT = 'Y',
  SPECIALTY = 'W',
  ATTACH_DOC = 'R',
  MEDICAL_CERT_OVERVIEW = 'X',
  MEDICAL_CERT_ELIGIBILITY = 'Y',
  MEDICAL_CERT_VERIFICATION = 'Z',
}

export enum StepTrackingId {
  ADDITIONAL = 'additional',
  ABOUT_PRACTICE = 'about-practice',
  SERVICE_AREA = 'service-area',
  BOOKING_TYPE = 'booking-type',
  CONFIRMATION = 'confirmation',
  DUPLICATE = 'duplicate',
  ATTACH_DOC = 'attach-doc',
  OVERVIEW = 'overview',
  PRACTICE_POLICY = 'practice-policy',
  PATIENT_DETAILS = 'patient-details',
  MEDICAL_CERT_OVERVIEW = 'med-cert-overview',
  MEDICAL_CERT_ELIGIBILITY = 'med-cert-eligibility',
  MEDICAL_CERT_VERIFICATION = 'med-cert-verification',
  VERIFY = 'verify',
  PAYMENT = 'payment',
  VERIFY_CAPTCHA = 'verify-captcha',
  PHARMACY = 'pharmacy',
  ADDITIONAL_PERSONAL_DETAILS = 'more-about-you',
  SPECIALTY_SELECTION = 'specialty-selection',
  APPOINTMENT_SELECTION = 'appointment-selection',
  SECOND_APPOINTMENT_TIME = 'second-appointment-time',
}

export const ProcessIdByStepTrackingId: { [trackingId: string]: ProcessId } = {
  [StepTrackingId.ABOUT_PRACTICE]: ProcessId.ABOUT_PRACTICE,
  [StepTrackingId.OVERVIEW]: ProcessId.APPOINTMENT_OVERVIEW,
  [StepTrackingId.APPOINTMENT_SELECTION]: ProcessId.APPOINTMENT_SELECTION,
  [StepTrackingId.BOOKING_TYPE]: ProcessId.APPOINTMENT_TYPE,
  [StepTrackingId.PRACTICE_POLICY]: ProcessId.POLICY,
  [StepTrackingId.PATIENT_DETAILS]: ProcessId.PERSONAL_DETAILS,
  [StepTrackingId.PAYMENT]: ProcessId.PAYMENT,
  [StepTrackingId.PHARMACY]: ProcessId.PHARMACY,
  [StepTrackingId.ADDITIONAL_PERSONAL_DETAILS]: ProcessId.ADDITIONAL_PERSONAL_DETAILS,
  [StepTrackingId.SPECIALTY_SELECTION]: ProcessId.SPECIALTY,
  [StepTrackingId.ATTACH_DOC]: ProcessId.ATTACH_DOC,
  [StepTrackingId.MEDICAL_CERT_OVERVIEW]: ProcessId.MEDICAL_CERT_OVERVIEW,
  [StepTrackingId.MEDICAL_CERT_ELIGIBILITY]: ProcessId.MEDICAL_CERT_ELIGIBILITY,
  [StepTrackingId.MEDICAL_CERT_VERIFICATION]: ProcessId.MEDICAL_CERT_VERIFICATION,
};

export enum RoadblockId {
  GET_CARE_VIA_TELEHEALTH = 'getCareViaTelehealth',
  CALL_PRACTICE = 'bf-callPractice-covidYes',
  NOT_ACCEPTING_NEW_PATIENTS = 'bf-notacceptingnewpatientsatpractice',
  NOT_ACCEPTING_EXISTING_PATIENTS = 'bf-notacceptingexistingpatientsatpractice',
}
